import { useState, useRef } from "react";

/**
 * useFormHybrid - A flexible hook that manages both stateful and ref-based form data.
 *
 * @param {Object} config
 * @param {Object} config.initialState - reactive state values (affect UI)
 * @param {Object} config.initialRefs - non-reactive values (do not affect UI)
 * @param {Function} config.onSubmit - async function to call with combined data
 *
 * @returns {{
 *   state: Object,
 *   setState: Function,
 *   refs: Object,
 *   handleChange: Function,
 *   handleSubmit: Function,
 *   isSubmitting: boolean
 * }}
 */
export function useFormHybrid({
  initialState = {},
  initialRefs = {},
  onSubmit,
}) {
  const [state, setState] = useState(initialState);
  const refs = useRef({ ...initialRefs });
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Writes an input into refs when `data-ref="true"`, otherwise into reactive state.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Field change event.
   * @returns {void}
   */
  const handleChange = (e) => {
    const { name, value, type, checked, dataset } = e.target;
    const isRef = dataset.ref === "true";

    if (isRef) {
      refs.current[name] = type === "checkbox" ? checked : value;
    } else {
      setState((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  /**
   * Merges state + refs and calls `onSubmit`.
   *
   * @param {React.FormEvent} [e] Optional form event to preventDefault.
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const payload = { ...state, ...refs.current };

    try {
      setIsSubmitting(true);
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { state, setState, refs, handleChange, handleSubmit, isSubmitting };
}
